<!-- Modal Overlay -->
<div
  *ngIf="isOpen && quote"
  class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
  (click)="close()"
>
  <!-- Modal Content -->
  <div
    class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-100 animate-slideUp"
    (click)="$event.stopPropagation()"
  >
    <!-- Modal Header -->
    <div
      class="sticky top-0 bg-gradient-to-r from-teal-50 to-blue-50 border-b border-gray-200 px-6 py-5 flex items-center justify-between z-10"
    >
      <div>
        <h2 class="text-2xl font-bold text-gray-900">
          {{ "QUOTE_DETAILS_MODAL.QUOTE_DETAILS" | translate }}
        </h2>
        <p class="text-sm text-gray-600 mt-1">
          {{ "QUOTE_DETAILS_MODAL.VIEW_COMPREHENSIVE_DETAILS" | translate }}
        </p>
      </div>
      <button
        (click)="close()"
        class="text-gray-400 hover:text-gray-600 hover:bg-white/80 rounded-full p-2 transition-all duration-200"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
      <!-- Provider Info Header -->
      <div
        class="flex items-center justify-between mb-6 pb-6 border-b border-gray-200"
      >
        <div class="flex items-center space-x-4">
          <div
            [class]="
              'w-16 h-16 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg ' +
              getProviderColor(quote.providerName)
            "
          >
            {{ getProviderInitials(quote.providerName) }}
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900 capitalize">
              {{ quote.providerName }}
              {{ "QUOTE_DETAILS_MODAL.INSURANCE" | translate }}
            </h3>
            <p class="text-sm text-gray-500 mt-1">
              {{ "QUOTE_DETAILS_MODAL.REFERENCE" | translate }}:
              <span class="font-medium text-gray-700">{{
                quote.quoteRequestReferenceNo
              }}</span>
            </p>
          </div>
        </div>
        <div
          class="text-end bg-gradient-to-br from-teal-50 to-teal-100 px-6 py-4 rounded-xl border border-teal-200"
        >
          <div
            class="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent"
          >
            {{ formatCurrency(quote.amount || quote.policyPremium) }}
            <span class="text-lg">{{
              "QUOTE_DETAILS_MODAL.SAR" | translate
            }}</span>
          </div>
          <p class="text-xs text-gray-600 mt-1 font-medium">
            {{ "QUOTE_DETAILS_MODAL.TOTAL_PREMIUM" | translate }}
          </p>
        </div>
      </div>

      <!-- Quote Summary -->
      <div
        class="mb-6 border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-gray-50 to-white shadow-sm"
      >
        <h3 class="text-lg font-bold text-gray-900 mb-5 flex items-center">
          <svg
            class="w-5 h-5 mr-2 text-teal-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          {{ "QUOTE_DETAILS_MODAL.QUOTE_SUMMARY" | translate }}
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <p
              class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
            >
              {{ "QUOTE_DETAILS_MODAL.REFERENCE_NUMBER" | translate }}
            </p>
            <p class="text-base font-semibold text-gray-900">
              {{ quote.quoteRequestReferenceNo }}
            </p>
          </div>

          <div class="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <p
              class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
            >
              {{ "QUOTE_DETAILS_MODAL.COVERAGE_TYPE" | translate }}
            </p>
            <p class="text-base font-semibold text-gray-900">
              {{ "QUOTE_DETAILS_MODAL.PREMIUM" | translate }}
            </p>
          </div>

          <div class="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <p
              class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
            >
              {{ "QUOTE_DETAILS_MODAL.BASE_PRICE" | translate }}
            </p>
            <p class="text-base font-semibold text-teal-700">
              {{ formatCurrency(quote.amount || quote.policyPremium) }}
              <span class="text-sm text-gray-500 ml-1">{{
                "QUOTE_DETAILS_MODAL.SAR" | translate
              }}</span>
            </p>
          </div>

          <div class="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
            <p
              class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
            >
              {{ "QUOTE_DETAILS_MODAL.NUMBER_OF_MEMBERS" | translate }}
            </p>
            <p class="text-base font-semibold text-gray-900">
              {{
                quote.membersBreakdown.length ||
                  quote.companyNumberOfEmployee ||
                  0
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Company Information & Members Section (Combined) -->
      <div
        class="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white"
      >
        <div
          class="bg-gradient-to-br from-gray-50 to-white p-6 border-b border-gray-200"
        >
          <h3 class="text-lg font-bold text-gray-900 flex items-center">
            <svg
              class="w-5 h-5 mr-2 text-teal-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            {{ "QUOTE_DETAILS_MODAL.COMPANY_INFORMATION" | translate }}
          </h3>
        </div>

        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <p
                class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
              >
                {{ "QUOTE_DETAILS_MODAL.COMPANY_NAME" | translate }}
              </p>
              <p class="text-base font-semibold text-gray-900">
                {{ quote.companyName }}
              </p>
            </div>

            <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <p
                class="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium"
              >
                {{ "QUOTE_DETAILS_MODAL.COMPANY_NAME_ARABIC" | translate }}
              </p>
              <p class="text-base font-semibold text-gray-900" dir="rtl">
                {{ quote.companyNameArabic }}
              </p>
            </div>
          </div>

          <!-- Members Breakdown Section -->
          <div
            *ngIf="quote.membersBreakdown && quote.membersBreakdown.length > 0"
          >
            <h4
              class="text-base font-bold text-gray-900 mb-4 flex items-center"
            >
              <svg
                class="w-5 h-5 mr-2 text-teal-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {{ "QUOTE_DETAILS_MODAL.MEMBERS_BREAKDOWN" | translate }}
            </h4>

            <!-- Scrollable Members List -->
            <div
              class="max-h-80 overflow-y-auto space-y-3 pr-2 custom-scrollbar"
            >
              <div
                *ngFor="let member of quote.membersBreakdown; let i = index"
                class="bg-gradient-to-br from-white to-gray-50 rounded-lg p-4 hover:shadow-md hover:scale-[1.01] transition-all duration-200 border border-gray-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <p class="text-base font-semibold text-gray-900">
                      {{
                        member.memberName ||
                          ("QUOTE_DETAILS_MODAL.MEMBER" | translate) +
                            " " +
                            (i + 1)
                      }}
                    </p>
                    <p class="text-sm text-gray-500 mt-1">
                      {{ "QUOTE_DETAILS_MODAL.MEMBER_ID" | translate }}:
                      <span class="font-medium text-gray-700">{{
                        member.MemberID
                      }}</span>
                    </p>
                    <div class="mt-2">
                      <span
                        [class]="
                          'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ' +
                          getInsuranceClassColor(member.MemberInsuranceClass)
                        "
                      >
                        {{ getInsuranceClassName(member.MemberInsuranceClass) }}
                      </span>
                    </div>
                  </div>

                  <div class="text-end ml-4">
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency(member.BreakdownAmount) }}
                    </p>
                    <p class="text-xs text-gray-500 font-medium">
                      {{ "QUOTE_DETAILS_MODAL.SAR" | translate }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Premium Section -->
            <div
              class="mt-6 bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-5 shadow-lg"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <svg
                    class="w-6 h-6 text-white mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <p
                      class="text-white/90 text-sm font-medium uppercase tracking-wide"
                    >
                      {{
                        "QUOTE_DETAILS_MODAL.TOTAL_MEMBERS_PREMIUM" | translate
                      }}
                    </p>
                    <p class="text-white/70 text-xs mt-0.5">
                      {{ quote.membersBreakdown.length }}
                      {{ "QUOTE_DETAILS_MODAL.MEMBERS" | translate }}
                    </p>
                  </div>
                </div>
                <div class="text-end">
                  <p class="text-3xl font-bold text-white">
                    {{ formatCurrency(getTotalMembersPremium()) }}
                  </p>
                  <p class="text-white/80 text-sm font-medium mt-1">
                    {{ "QUOTE_DETAILS_MODAL.SAR" | translate }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
